import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import {
  Activity,
  Loader2,
  User,
  Calendar,
  Search,
  Download,
  ChevronDown,
  Users,
  Check,
  HardDrive,
  Youtube,
  AlertCircle,
  Monitor,
  Globe,
  Hourglass,
  Clock,
  AppWindow,
  Briefcase,
  Smartphone,
  Timer,
  BarChart3,
  CalendarRange,
  ChevronRight,
  X,
  Play,
  Pause,
  Folder,
  FileText,
  Music,
  Image,
  Video,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "../hooks/useToast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import api from "../api/api";
import { getEmployees } from "../api/employees";

/* ─── Types ─────────────────────────────────────────────── */
interface AppUsage {
  app_name: string;
  duration_mins: number;
}
interface MediaDetected {
  url?: string;
  app_name?: string;
  end_time?: string;
  start_time?: string;
  media_title?: string;
  duration_mins?: number;
}
interface IdlePeriod {
  type: string;
  end_time: string;
  start_time: string;
  duration_mins: number;
}
interface TimelineEvent {
  url: string | null;
  time: string;
  type: string;
  event: string | null;
  label: string | null;
  media: any | null;
  title: string | null;
  app_exe: string | null;
  app_name: string | null;
  end_time: string;
  duration_mins: number;
  file_path?: string | null;
}
interface ActivityData {
  id: string;
  employeeID: string;
  employee_name: string;
  date: string;
  first_activity: string;
  last_activity: string;
  total_tracked_mins: number;
  active_mins: number;
  idle_mins: number;
  shutdown_mins: number;
  app_usage: AppUsage[];
  browser_usage: any[];
  media_detected: MediaDetected[];
  idle_periods: IdlePeriod[];
  timeline: TimelineEvent[];
  full_summary: any[];
  user_id: string;
  headOfficeID: string;
  masterID: string;
  branchID: string;
  level: number;
  generated_at: string | null;
  created_at: string | null;
  last_updated: string | null;
  updated_at: string;
}
interface ApiResponse {
  success: boolean;
  total: number;
  returned: number;
  limit: number;
  offset: number;
  monthly_summary?: {
    total_active_mins: number;
    total_idle_mins: number;
    total_tracked_mins: number;
    total_active_hrs: number;
    total_idle_hrs: number;
    total_tracked_hrs: number;
  };
  data: ActivityData[];
}
interface Employee {
  employeeID: string;
  employee_name: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
}
interface EmployeeSummary {
  employeeID: string;
  employee_name: string;
  total_tracked_mins: number;
  active_mins: number;
  idle_mins: number;
  data: ActivityData[];
}
interface BrowserSite {
  key: string;
  url: string;
  duration_mins: number;
  title: string;
  visits: number;
}
interface MediaItem {
  id: string;
  title: string;
  url: string;
  app_name: string;
  start_time: string;
  end_time: string;
  duration_mins: number;
  fmt_start: string;
  fmt_end: string;
  fmt_dur: string;
}
interface AppUpdate {
  id: string;
  name: string;
  version: string;
  size: string;
  updatedDate: string;
  downloadUrl: string;
}

/* ─── Helpers ────────────────────────────────────────────── */
const fmtTime = (dt: string) => {
  try {
    return format(new Date(dt), "HH:mm:ss");
  } catch {
    return dt;
  }
};
const fmtDate = (d: string) => {
  try {
    return format(new Date(d), "MMM dd, yyyy");
  } catch {
    return d;
  }
};
const fmtMins = (m: number) => {
  const hours = Math.floor(m / 60);
  const minutes = Math.round(m % 60);
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};
const fmtDur = (m: number) => {
  if (!m) return "0:00";
  const mins = Math.floor(m);
  const secs = Math.floor((m - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const truncateText = (text: string, maxLength: number = 60) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

// Don't truncate file paths - show full path
const formatFilePath = (path: string) => {
  if (!path) return "";
  return path;
};

const APP_COLORS: Record<string, string> = {
  "Whatsapp.root": "#25D366",
  Chrome: "#4285F4",
  Explorer: "#0078D4",
  Acrobat: "#EE3E34",
  "Excel.exe": "#217346",
  "Winword.exe": "#185ABD",
  Workmoniter: "#6366F1",
  Photos: "#00A4EF",
  Notepad: "#9E9E9E",
};
const appColor = (n: string) => APP_COLORS[n] || "#8b5cf6";

const getAppIcon = (appName: string) => {
  const name = appName?.toLowerCase() || "";
  if (name.includes("whatsapp")) return "💬";
  if (name.includes("chrome")) return "🌐";
  if (name.includes("brave")) return "🦁";
  if (name.includes("explorer")) return "📁";
  if (name.includes("acrobat") || name.includes("pdf")) return "📄";
  if (name.includes("excel")) return "📊";
  if (name.includes("word")) return "📝";
  if (name.includes("photo")) return "🖼️";
  if (name.includes("terminal") || name.includes("cmd")) return "💻";
  if (name.includes("code")) return "👨‍💻";
  return "📱";
};

const getMediaIcon = (appName: string) => {
  const name = appName?.toLowerCase() || "";
  if (name.includes("youtube")) return "▶️";
  if (name.includes("spotify")) return "🎵";
  if (name.includes("netflix")) return "🎬";
  if (name.includes("prime")) return "📺";
  if (name.includes("hotstar")) return "🏏";
  return "🎥";
};

const getDisplayNameFromUrl = (url: string): string => {
  if (!url) return "";
  try {
    let cleanUrl = url.replace(/^https?:\/\//, "").replace(/^www\./, "");
    const domain = cleanUrl.split("/")[0];
    return domain;
  } catch {
    return url;
  }
};

const isFilePath = (path: string): boolean => {
  if (!path) return false;
  const fileExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".txt",
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".mp4",
    ".mp3",
    ".zip",
    ".rar",
    ".tmp",
    ".py",
    ".js",
    ".html",
    ".css",
    ".json",
    ".xml",
    ".log",
  ];
  return (
    fileExtensions.some((ext) => path.toLowerCase().includes(ext)) ||
    path.includes("/uploads/") ||
    path.includes("/files/") ||
    path.includes("/documents/") ||
    path.includes("C:\\") ||
    path.includes(":\\") ||
    path.startsWith("file://")
  );
};

const getFileIcon = (path: string) => {
  if (!path) return <FileText style={{ width: 12, height: 12 }} />;
  const lowerPath = path.toLowerCase();
  if (lowerPath.includes(".pdf"))
    return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".doc") || lowerPath.includes(".docx"))
    return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".xls") || lowerPath.includes(".xlsx"))
    return <FileText style={{ width: 12, height: 12 }} />;
  if (
    lowerPath.includes(".jpg") ||
    lowerPath.includes(".jpeg") ||
    lowerPath.includes(".png") ||
    lowerPath.includes(".gif")
  )
    return <Image style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".mp3") || lowerPath.includes(".wav"))
    return <Music style={{ width: 12, height: 12 }} />;
  if (
    lowerPath.includes(".mp4") ||
    lowerPath.includes(".avi") ||
    lowerPath.includes(".mkv")
  )
    return <Video style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".py"))
    return <FileText style={{ width: 12, height: 12 }} />;
  if (lowerPath.includes(".js"))
    return <FileText style={{ width: 12, height: 12 }} />;
  return <FileText style={{ width: 12, height: 12 }} />;
};

const getFileName = (path: string): string => {
  if (!path) return "";
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1];
};

// Clickable Path Component - Shows FULL path
const ClickablePath: React.FC<{ path: string; children?: React.ReactNode }> = ({
  path,
  children,
}) => {
  if (!path)
    return (
      <span style={{ fontSize: 11, color: "#94a3b8" }}>No path available</span>
    );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (path.match(/^[A-Za-z]:\\/)) {
      const fileUrl = `file:///${path.replace(/\\/g, "/")}`;
      window.open(fileUrl, "_blank");
    } else if (path.startsWith("file://")) {
      window.open(path, "_blank");
    } else if (path.startsWith("http://") || path.startsWith("https://")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      window.open(path, "_blank");
    }
  };

  const isFile = isFilePath(path);
  // Show FULL path, not truncated
  const displayText = children || formatFilePath(path);

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{
        fontSize: 11,
        color: "#3b82f6",
        wordBreak: "break-all",
        textDecoration: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
    >
      {isFile && getFileIcon(path)}
      <span style={{ fontFamily: "monospace", fontSize: 10 }}>
        {displayText}
      </span>
      <span style={{ fontSize: 9, opacity: 0.7 }}>🔗</span>
    </a>
  );
};

/* ─── Chip Component ───────────────────────────────────── */
const Chip: React.FC<{ label: string; value: string; valueClass?: string }> = ({
  label,
  value,
  valueClass = "text-slate-800",
}) => (
  <div
    style={{
      background: "#f8fafc",
      borderRadius: 12,
      padding: "6px 12px",
      textAlign: "center",
      minWidth: 52,
    }}
  >
    <p
      style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1, marginBottom: 4 }}
    >
      {label}
    </p>
    <p
      style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}
      className={valueClass}
    >
      {value}
    </p>
  </div>
);

/* ─── Timeline Details Component (Shows below clicked app) ─── */
const AppTimelineDetails: React.FC<{
  appName: string;
  timeline: TimelineEvent[];
  onClose: () => void;
}> = ({ appName, timeline, onClose }) => {
  const filteredEvents = timeline.filter(
    (event) =>
      event.app_name && event.app_name.toLowerCase() === appName.toLowerCase(),
  );

  const totalDuration = filteredEvents.reduce(
    (sum, e) => sum + (e.duration_mins || 0),
    0,
  );
  const activeCount = filteredEvents.filter((e) => e.type === "active").length;

  if (filteredEvents.length === 0) {
    return (
      <div
        style={{
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: 12,
          padding: 20,
          textAlign: "center",
          marginTop: 12,
        }}
      >
        <AlertCircle
          style={{
            width: 20,
            height: 20,
            color: "#dc2626",
            margin: "0 auto 8px",
          }}
        />
        <p style={{ fontSize: 12, color: "#dc2626" }}>
          No timeline data found for {appName}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: 8,
        marginRight: 8,
        background: "#faf5ff",
        borderRadius: 12,
        border: "1px solid #e9d5ff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          background: "#f3e8ff",
          borderBottom: "1px solid #e9d5ff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{getAppIcon(appName)}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#6b21a5" }}>
            {appName}
          </span>
          <span
            style={{
              fontSize: 10,
              color: "#9333ea",
              background: "#e9d5ff",
              padding: "2px 8px",
              borderRadius: 20,
            }}
          >
            {filteredEvents.length} events
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            borderRadius: 20,
            padding: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            color: "#6b21a5",
          }}
        >
          <X style={{ width: 14, height: 14 }} /> Close
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          padding: "10px 16px",
          background: "#fff",
          borderBottom: "1px solid #f3e8ff",
        }}
      >
        <div>
          <p style={{ fontSize: 10, color: "#94a3b8" }}>Total Time</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#7c3aed" }}>
            {fmtMins(totalDuration)}
          </p>
        </div>
        <div>
          <p style={{ fontSize: 10, color: "#94a3b8" }}>Active Sessions</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#16a34a" }}>
            {activeCount}
          </p>
        </div>
        <div>
          <p style={{ fontSize: 10, color: "#94a3b8" }}>Avg Duration</p>
          <p style={{ fontSize: 16, fontWeight: 700, color: "#f97316" }}>
            {filteredEvents.length > 0
              ? fmtMins(totalDuration / filteredEvents.length)
              : "0m"}
          </p>
        </div>
      </div>

      <div style={{ maxHeight: 350, overflowY: "auto", padding: "8px 16px" }}>
        {filteredEvents.map((event, idx) => (
          <div
            key={`${event.time}-${idx}`}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: "10px 0",
              borderBottom:
                idx === filteredEvents.length - 1
                  ? "none"
                  : "1px solid #f3e8ff",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span
                style={{
                  fontSize: 10,
                  fontFamily: "monospace",
                  color: "#64748b",
                  width: 60,
                  flexShrink: 0,
                }}
              >
                {fmtTime(event.time)}
              </span>
              <span
                style={{
                  fontSize: 9,
                  padding: "2px 6px",
                  borderRadius: 10,
                  fontWeight: 500,
                  background:
                    event.type === "idle"
                      ? "#fff7ed"
                      : event.type === "system_event"
                        ? "#f1f5f9"
                        : "#dcfce7",
                  color:
                    event.type === "idle"
                      ? "#f97316"
                      : event.type === "system_event"
                        ? "#64748b"
                        : "#16a34a",
                  flexShrink: 0,
                  width: 65,
                  textAlign: "center",
                }}
              >
                {event.type === "idle"
                  ? "Idle"
                  : event.type === "system_event"
                    ? "System"
                    : "Active"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {event.title && (
                  <p
                    style={{
                      fontSize: 11,
                      color: "#1e293b",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {truncateText(event.title, 70)}
                  </p>
                )}
                {event.label &&
                  event.label !== "SESSION_START" &&
                  event.label !== "SHUTDOWN" && (
                    <p style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                      {event.label}
                    </p>
                  )}
              </div>
              {event.duration_mins > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: "#7c3aed",
                    flexShrink: 0,
                  }}
                >
                  {fmtMins(event.duration_mins)}
                </span>
              )}
            </div>

            {/* Show URL if present - FULL URL */}
            {event.url &&
              event.url !== "browser" &&
              event.url !== "unknown" &&
              event.url !== "null" && (
                <div style={{ marginLeft: 70 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <Globe
                      style={{ width: 11, height: 11, color: "#94a3b8" }}
                    />
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>URL:</span>
                    <ClickablePath path={event.url} />
                  </div>
                </div>
              )}

            {/* Show file_path if present - FULL FILE PATH */}
            {event.file_path && (
              <div style={{ marginLeft: 70 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <Folder style={{ width: 11, height: 11, color: "#f59e0b" }} />
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>
                    File Path:
                  </span>
                  <ClickablePath path={event.file_path} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Clickable App Row Component ─── */
const ClickableAppRow: React.FC<{
  app: AppUsage;
  max: number;
  timeline: TimelineEvent[];
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ app, max, timeline, isExpanded, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const appTimelineEvents = timeline.filter(
    (e) =>
      e.app_name && e.app_name.toLowerCase() === app.app_name.toLowerCase(),
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          cursor: "pointer",
          borderRadius: 10,
          transition: "all 0.2s ease",
          background: isExpanded
            ? "#ede9fe"
            : isHovered
              ? "#f8fafc"
              : "transparent",
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>
          {getAppIcon(app.app_name)}
        </span>
        <span
          style={{
            fontSize: 12,
            color: isExpanded ? "#7c3aed" : "#475569",
            width: 110,
            flexShrink: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: isExpanded ? 600 : 400,
          }}
        >
          {app.app_name}
        </span>
        <div
          style={{
            flex: 1,
            height: 7,
            background: "#f1f5f9",
            borderRadius: 99,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 99,
              background: isExpanded ? "#7c3aed" : appColor(app.app_name),
              width: `${max > 0 ? (app.duration_mins / max) * 100 : 0}%`,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#1e293b",
            minWidth: 55,
            textAlign: "right",
            flexShrink: 0,
            background: "#f1f5f9",
            padding: "2px 8px",
            borderRadius: 20,
          }}
        >
          {fmtMins(app.duration_mins)}
        </span>
        {appTimelineEvents.length > 0 && (
          <span
            style={{
              fontSize: 9,
              background: "#e2e8f0",
              padding: "2px 6px",
              borderRadius: 20,
              color: "#64748b",
              flexShrink: 0,
            }}
          >
            {appTimelineEvents.length}
          </span>
        )}
        <ChevronRight
          style={{
            width: 14,
            height: 14,
            color: isExpanded ? "#7c3aed" : "#cbd5e1",
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      </div>
      {isExpanded && (
        <AppTimelineDetails
          appName={app.app_name}
          timeline={timeline}
          onClose={onToggle}
        />
      )}
    </div>
  );
};

/* ─── Clickable Media Row Component (with dropdown) ─── */
const ClickableMediaRow: React.FC<{
  media: MediaItem;
  max: number;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ media, max, isExpanded, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const platform = media.app_name || "Unknown";

  const isYouTube =
    media.url?.toLowerCase().includes("youtube.com") ||
    media.url?.toLowerCase().includes("youtu.be") ||
    media.title?.toLowerCase().includes("youtube") ||
    platform.toLowerCase().includes("youtube");

  const isFile = isFilePath(media.url);

  const getDisplayTitle = () => {
    if (
      media.title &&
      media.title !== "Media" &&
      media.title !== "Unknown" &&
      media.title !== media.url
    ) {
      return media.title;
    }
    if (media.url && media.url !== "browser") {
      if (isFile) {
        return getFileName(media.url);
      }
      return getDisplayNameFromUrl(media.url);
    }
    return "Media";
  };

  const displayTitle = getDisplayTitle();

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          cursor: "pointer",
          borderRadius: 10,
          transition: "all 0.2s ease",
          background: isExpanded
            ? "#fef3c7"
            : isHovered
              ? "#fffbeb"
              : "transparent",
        }}
        onClick={onToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>
          {getMediaIcon(platform)}
        </span>
        <span
          style={{
            fontSize: 12,
            color: isExpanded ? "#d97706" : "#475569",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: isExpanded ? 600 : 400,
          }}
        >
          {isYouTube ? "YouTube" : truncateText(displayTitle, 50)}
        </span>
        <div
          style={{
            width: 80,
            height: 6,
            background: "#f1f5f9",
            borderRadius: 99,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 99,
              background: isExpanded ? "#d97706" : "#ef4444",
              width: `${max > 0 ? (media.duration_mins / max) * 100 : 0}%`,
            }}
          />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#1e293b",
            minWidth: 55,
            textAlign: "right",
            flexShrink: 0,
            background: "#fef3c7",
            padding: "2px 8px",
            borderRadius: 20,
          }}
        >
          {fmtMins(media.duration_mins)}
        </span>
        <span
          style={{
            fontSize: 9,
            background: "#fef3c7",
            padding: "2px 6px",
            borderRadius: 20,
            color: "#d97706",
            flexShrink: 0,
          }}
        >
          {platform}
        </span>
        <ChevronRight
          style={{
            width: 14,
            height: 14,
            color: isExpanded ? "#d97706" : "#cbd5e1",
            flexShrink: 0,
            transition: "transform 0.2s ease",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
        />
      </div>

      {/* Expanded Media Details */}
      {isExpanded && (
        <div
          style={{
            marginTop: 8,
            marginLeft: 8,
            marginRight: 8,
            marginBottom: 8,
            background: "#fffbeb",
            borderRadius: 12,
            border: "1px solid #fde68a",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
              background: "#fef3c7",
              borderBottom: "1px solid #fde68a",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isFile ? (
                getFileIcon(media.url)
              ) : (
                <Youtube style={{ width: 14, height: 14, color: "#ef4444" }} />
              )}
              <span style={{ fontSize: 12, fontWeight: 600, color: "#92400e" }}>
                {isFile
                  ? "File Details"
                  : isYouTube
                    ? "YouTube Media Details"
                    : "Media Details"}
              </span>
            </div>
          </div>

          <div
            style={{
              padding: "12px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              <div>
                <p style={{ fontSize: 10, color: "#94a3b8" }}>Start Time</p>
                <p style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>
                  {media.fmt_start}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: "#94a3b8" }}>End Time</p>
                <p style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>
                  {media.fmt_end}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: "#94a3b8" }}>Duration</p>
                <p style={{ fontSize: 12, fontWeight: 600, color: "#d97706" }}>
                  {media.fmt_dur}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 10, color: "#94a3b8" }}>Platform</p>
                <p style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>
                  {platform}
                </p>
              </div>
            </div>

            {/* Clickable URL/File Path - FULL PATH */}
            {media.url &&
              media.url !== "browser" &&
              media.url !== "unknown" && (
                <div>
                  <p
                    style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}
                  >
                    {isFile ? "File Path" : "URL"}
                  </p>
                  <ClickablePath path={media.url} />
                </div>
              )}
//
            {/* Media Title if available */}
            {media.title &&  
              media.title !== "Media" &&
              media.title !== "Unknown" && (
                <div>
                  <p style={{ fontSize: 10, color: "#94a3b8" }}>Title</p>
                  <p
                    style={{ fontSize: 11, color: "#475569", fontWeight: 500 }}
                  >
                    {media.title}
                  </p>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Section Title with Total Time Component ────────────────── */
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  totalMins: number;
  color?: string;
}> = ({ icon, title, totalMins, color = "#7c3aed" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon}
      <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
        {title}
      </span>
    </div>
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: color,
        background: `${color}15`,
        padding: "4px 10px",
        borderRadius: 20,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <Timer style={{ width: 12, height: 12 }} />
      Total: {fmtMins(totalMins)}
    </span>
  </div>
);

/* ─── App Update Card ──────────────────────────────────── */
const AppUpdateCard: React.FC<{ app: AppUpdate }> = ({ app }) => {
  const [dl, setDl] = useState(false);
  const { toast } = useToast();
  const handleDl = async () => {
    setDl(true);
    try {
      await new Promise((r) => setTimeout(r, 1500));
      const a = document.createElement("a");
      a.href = app.downloadUrl;
      a.download = `${app.name}_${app.version}.exe`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({
        title: "Download Started",
        description: `${app.name} ${app.version} downloading…`,
      });
    } catch {
      toast({
        title: "Download Failed",
        description: "Try again.",
        variant: "destructive",
      });
    } finally {
      setDl(false);
    }
  };
  const icon =
    app.name.toLowerCase().includes("hr") ||
    app.name.toLowerCase().includes("payroll") ? (
      <Briefcase className="h-6 w-6 text-blue-500" />
    ) : app.name.toLowerCase().includes("monitor") ||
      app.name.toLowerCase().includes("work") ? (
      <Monitor className="h-6 w-6 text-violet-500" />
    ) : (
      <Smartphone className="h-6 w-6 text-slate-400" />
    );
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 16,
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #f1f5f9",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1e293b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {app.name}
          </span>
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 99,
              background: "#f0fdf4",
              color: "#16a34a",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Latest
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
            fontSize: 11,
            color: "#94a3b8",
          }}
        >
          <span>v{app.version}</span>
          <span>·</span>
          <HardDrive style={{ width: 11, height: 11 }} />
          <span>{app.size}</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 8,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "#94a3b8",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Calendar style={{ width: 11, height: 11 }} />
            Updated {app.updatedDate}
          </span>
          <button
            onClick={handleDl}
            disabled={dl}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              height: 28,
              padding: "0 12px",
              background: "#0f172a",
              color: "#fff",
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              flexShrink: 0,
              opacity: dl ? 0.7 : 1,
            }}
          >
            {dl ? (
              <Loader2
                style={{ width: 11, height: 11 }}
                className="animate-spin"
              />
            ) : (
              <Download style={{ width: 11, height: 11 }} />
            )}
            {dl ? "…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ────────────────────────────────────── */
export const ActivityDashboard: React.FC = () => {
  const [selEmp, setSelEmp] = useState<Employee | null>(null);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [filteredData, setFilteredData] = useState<ActivityData[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empSearch, setEmpSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"timeline" | "summary">("timeline");
  const [openDrop, setOpenDrop] = useState(false);
  const [apiResp, setApiResp] = useState<ApiResponse | null>(null);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const [expandedMedia, setExpandedMedia] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const appUpdates: AppUpdate[] = [
    {
      id: "1",
      name: "HR Payroll",
      version: "3.2.1",
      size: "10 MB",
      updatedDate: "15/01/2026",
      downloadUrl: "https://api-hr.startupkhata.com/download_hr_app/hr_payroll",
    },
    {
      id: "2",
      name: "Work Monitor",
      version: "2.5.0",
      size: "136 MB",
      updatedDate: "24/03/2026",
      downloadUrl:
        "https://api-hr.startupkhata.com/download_hr_app/work_monitor",
    },
  ];

  useEffect(() => {
    getEmployees()
      .then((r) => setEmployees(r?.employees || []))
      .catch(console.error);
    (async () => {
      setLoadingEmps(true);
      try {
        const r = await api.get("/api/system-level-tracking/employees", {
          params: { limit: 100, offset: 0 },
        });
        const res: ApiResponse = r.data;
        if (res.success && res.data) {
          // Process if needed
        }
      } catch {
        /* silent */
      } finally {
        setLoadingEmps(false);
      }
    })();
  }, []);

  const fetchData = async () => {
    if (!selEmp) {
      toast({
        title: "Error",
        description: "Please select an employee",
        variant: "destructive",
      });
      return;
    }
    if (startDate > endDate) {
      toast({
        title: "Error",
        description: "Start date cannot be after end date",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    setError(null);
    setExpandedApps(new Set());
    setExpandedMedia(new Set());

    try {
      const r = await api.get("/api/system-level-tracking/employees", {
        params: {
          employee_id: selEmp.employeeID,
          start_date: startDate,
          end_date: endDate,
          limit: 50,
          offset: 0,
        },
      });
      const res: ApiResponse = r.data;
      setApiResp(res);
      if (res.success && res.data?.length > 0) {
        setFilteredData(res.data);
        toast({
          title: "Success",
          description: `Found ${res.data.length} record(s) for ${selEmp.first_name}`,
        });
      } else {
        setFilteredData([]);
        setError("No data found for the selected period");
        toast({
          title: "No Data",
          description: "No activity records found",
          variant: "destructive",
        });
      }
    } catch {
      setError("Failed to fetch activity data.");
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleApp = (sessionId: string, appName: string) => {
    const key = `${sessionId}-${appName}`;
    const newExpanded = new Set(expandedApps);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedApps(newExpanded);
  };

  const toggleMedia = (sessionId: string, mediaId: string) => {
    const key = `${sessionId}-${mediaId}`;
    const newExpanded = new Set(expandedMedia);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedMedia(newExpanded);
  };

  const isAppExpanded = (sessionId: string, appName: string) => {
    return expandedApps.has(`${sessionId}-${appName}`);
  };

  const isMediaExpanded = (sessionId: string, mediaId: string) => {
    return expandedMedia.has(`${sessionId}-${mediaId}`);
  };

  const filteredEmps = useMemo(() => {
    if (!empSearch) return employees;
    const s = empSearch.toLowerCase();
    return employees.filter(
      (e) =>
        e.first_name?.toLowerCase().includes(s) ||
        e.last_name?.toLowerCase().includes(s) ||
        e.employeeID.toLowerCase().includes(s),
    );
  }, [employees, empSearch]);

  const getBrowserSites = (session: ActivityData): BrowserSite[] => {
    const map = new Map<string, BrowserSite>();
    session.timeline.forEach((ev) => {
      if ((ev.app_name === "Chrome" || ev.app_name === "Brave") && ev.title) {
        let t = ev.title;
        if (ev.app_name === "Chrome") t = t.replace(" - Google Chrome", "");
        if (ev.app_name === "Brave") t = t.replace(" - Brave", "");
        if (!map.has(t))
          map.set(t, {
            key: t,
            url: ev.url || "",
            duration_mins: ev.duration_mins || 0,
            title: t,
            visits: 1,
          });
        else {
          const ex = map.get(t)!;
          ex.duration_mins += ev.duration_mins || 0;
          ex.visits++;
        }
      }
    });
    return Array.from(map.values()).sort(
      (a, b) => b.duration_mins - a.duration_mins,
    );
  };

  const getMedia = (session: ActivityData): MediaItem[] => {
    const items: MediaItem[] = session.media_detected
      .filter((m) => m.media_title || m.url)
      .map((m, i) => ({
        id: `${session.id}-${i}`,
        title: m.media_title || m.url?.split("/").pop() || "Media",
        url: m.url || "",
        app_name: m.app_name || "Unknown",
        start_time: m.start_time || "",
        end_time: m.end_time || "",
        duration_mins: m.duration_mins || 0,
        fmt_start: m.start_time ? fmtTime(m.start_time) : "N/A",
        fmt_end: m.end_time ? fmtTime(m.end_time) : "N/A",
        fmt_dur: fmtDur(m.duration_mins || 0),
      }));
    const uMap = new Map<string, MediaItem>();
    items.forEach((it) => {
      const k = it.start_time || it.title;
      if (!uMap.has(k) || uMap.get(k)!.duration_mins < it.duration_mins)
        uMap.set(k, it);
    });
    return Array.from(uMap.values()).sort(
      (a, b) => b.duration_mins - a.duration_mins,
    );
  };

  const empSummaries = (): EmployeeSummary[] => {
    const map = new Map<string, EmployeeSummary>();
    filteredData.forEach((it) => {
      if (!map.has(it.employeeID))
        map.set(it.employeeID, {
          employeeID: it.employeeID,
          employee_name: it.employee_name,
          total_tracked_mins: 0,
          active_mins: 0,
          idle_mins: 0,
          data: [],
        });
      const s = map.get(it.employeeID)!;
      s.total_tracked_mins += it.total_tracked_mins;
      s.active_mins += it.active_mins;
      s.idle_mins += it.idle_mins;
      s.data.push(it);
    });
    return Array.from(map.values());
  };

  const totalTracked = filteredData.reduce(
    (s, i) => s + i.total_tracked_mins,
    0,
  );
  const totalActive = filteredData.reduce((s, i) => s + i.active_mins, 0);
  const totalIdle = filteredData.reduce((s, i) => s + i.idle_mins, 0);
  const empName = selEmp
    ? `${selEmp.first_name || ""} ${selEmp.last_name || ""}`.trim() ||
      selEmp.employee_name
    : "";

  const card = {
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #f1f5f9",
    overflow: "hidden" as const,
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        background: "#f7f8fa",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          width: "100%",
          background: "#fff",
          borderBottom: "1px solid #f1f5f9",
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <div style={{ maxWidth: 1152, margin: "0 auto", padding: "10px 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "#7c3aed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Activity style={{ width: 14, height: 14, color: "#fff" }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
              Activity Report
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                borderRadius: 10,
                padding: 3,
                gap: 2,
              }}
            >
              {(["timeline", "summary"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => {
                    setView(v);
                    setExpandedApps(new Set());
                    setExpandedMedia(new Set());
                  }}
                  style={{
                    padding: "5px 12px",
                    fontSize: 11,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    background: view === v ? "#fff" : "transparent",
                    color: view === v ? "#1e293b" : "#64748b",
                    boxShadow:
                      view === v ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  {v}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: "6px 10px",
              }}
            >
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  fontSize: 11,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  width: 110,
                }}
              />
              <span style={{ color: "#cbd5e1", fontSize: 11 }}>→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  fontSize: 11,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  width: 110,
                }}
              />
            </div>

            <Popover open={openDrop} onOpenChange={setOpenDrop}>
              <PopoverTrigger asChild>
                <button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    height: 36,
                    padding: "0 12px",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    fontSize: 11,
                    cursor: "pointer",
                    minWidth: 120,
                    maxWidth: 180,
                  }}
                >
                  <User style={{ width: 12, height: 12, color: "#94a3b8" }} />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {empName || "Select employee"}
                  </span>
                  <ChevronDown
                    style={{ width: 12, height: 12, color: "#94a3b8" }}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search…"
                    value={empSearch}
                    onValueChange={setEmpSearch}
                    className="h-8 text-xs"
                  />
                  <CommandList>
                    <CommandEmpty className="text-xs py-2">
                      {loadingEmps ? "Loading…" : "No employee found."}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredEmps.map((e) => (
                        <CommandItem
                          key={e.employeeID}
                          value={`${e.first_name || ""} ${e.last_name || ""} ${e.employeeID}`}
                          onSelect={() => {
                            setSelEmp(e);
                            setEmpSearch("");
                            setOpenDrop(false);
                          }}
                          className="cursor-pointer text-xs"
                        >
                          <Check
                            className={`mr-2 h-3 w-3 ${selEmp?.employeeID === e.employeeID ? "opacity-100" : "opacity-0"}`}
                          />
                          <div>
                            <p className="font-medium">
                              {e.first_name} {e.last_name}
                            </p>
                            <p className="text-[10px] text-slate-500">
                              {e.employeeID}
                            </p>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <button
              onClick={fetchData}
              disabled={loading || !selEmp}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                height: 36,
                padding: "0 16px",
                background: "#0f172a",
                color: "#fff",
                fontSize: 11,
                fontWeight: 500,
                borderRadius: 10,
                border: "none",
                cursor: loading || !selEmp ? "not-allowed" : "pointer",
                opacity: loading || !selEmp ? 0.5 : 1,
              }}
            >
              {loading ? (
                <Loader2
                  style={{ width: 13, height: 13 }}
                  className="animate-spin"
                />
              ) : (
                <Search style={{ width: 13, height: 13 }} />
              )}
              Load
            </button>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div style={{ maxWidth: 1152, margin: "0 auto", padding: "20px 16px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: 12,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 12,
                color: "#dc2626",
                fontSize: 12,
              }}
            >
              <AlertCircle style={{ width: 14, height: 14 }} />
              <span>{error}</span>
            </div>
          )}

          {apiResp?.monthly_summary && (
            <div style={card}>
              <div style={{ padding: 20 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <BarChart3
                      style={{ width: 14, height: 14, color: "#7c3aed" }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Monthly Summary
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 12,
                  }}
                >
                  {[
                    {
                      label: "Total Tracked",
                      val: fmtMins(apiResp.monthly_summary.total_tracked_mins),
                      sub: `${apiResp.monthly_summary.total_tracked_hrs.toFixed(2)} hrs`,
                      c: "#7c3aed",
                    },
                    {
                      label: "Active Time",
                      val: fmtMins(apiResp.monthly_summary.total_active_mins),
                      sub: `${apiResp.monthly_summary.total_active_hrs.toFixed(2)} hrs`,
                      c: "#16a34a",
                    },
                    {
                      label: "Idle Time",
                      val: fmtMins(apiResp.monthly_summary.total_idle_mins),
                      sub: `${apiResp.monthly_summary.total_idle_hrs.toFixed(2)} hrs`,
                      c: "#f97316",
                    },
                    {
                      label: "Efficiency",
                      c: "#2563eb",
                      sub: "of tracked time",
                      val: `${apiResp.monthly_summary.total_tracked_mins > 0 ? ((apiResp.monthly_summary.total_active_mins / apiResp.monthly_summary.total_tracked_mins) * 100).toFixed(0) : 0}%`,
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 16,
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 10,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          marginBottom: 4,
                        }}
                      >
                        {s.label}
                      </p>
                      <p
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: s.c,
                          lineHeight: 1,
                        }}
                      >
                        {s.val}
                      </p>
                      <p
                        style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}
                      >
                        {s.sub}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div style={card}>
            <div style={{ padding: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Download
                    style={{ width: 14, height: 14, color: "#3b82f6" }}
                  />
                  <span
                    style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}
                  >
                    App Updates
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    padding: "3px 8px",
                    borderRadius: 99,
                    background: "#f1f5f9",
                    color: "#64748b",
                  }}
                >
                  {appUpdates.length} updates
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 12,
                }}
              >
                {appUpdates.map((a) => (
                  <AppUpdateCard key={a.id} app={a} />
                ))}
              </div>
            </div>
          </div>

          {!selEmp && !loading && (
            <div style={{ ...card, padding: 48, textAlign: "center" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 16,
                  background: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <Users style={{ width: 20, height: 20, color: "#94a3b8" }} />
              </div>
              <p style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
                Select an Employee
              </p>
              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                Choose an employee and date range to view activity
              </p>
            </div>
          )}

          {selEmp && !loading && filteredData.length === 0 && !error && (
            <div style={{ ...card, padding: 48, textAlign: "center" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 16,
                  background: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <Search style={{ width: 20, height: 20, color: "#94a3b8" }} />
              </div>
              <p style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
                No Activity Data
              </p>
              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                No records found for the selected period
              </p>
            </div>
          )}

          {loading && (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 64 }}
            >
              <Loader2
                className="animate-spin"
                style={{ width: 24, height: 24, color: "#7c3aed" }}
              />
            </div>
          )}

          {filteredData.length > 0 && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 12,
                }}
              >
                {[
                  {
                    label: "Records",
                    val: String(filteredData.length),
                    c: "#1e293b",
                  },
                  {
                    label: "Total Tracked",
                    val: fmtMins(totalTracked),
                    c: "#1e293b",
                  },
                  { label: "Active", val: fmtMins(totalActive), c: "#16a34a" },
                  { label: "Idle", val: fmtMins(totalIdle), c: "#f97316" },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: "#fff",
                      borderRadius: 16,
                      border: "1px solid #f1f5f9",
                      padding: 16,
                    }}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                      }}
                    >
                      {s.label}
                    </p>
                    <p
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: s.c,
                        marginTop: 4,
                      }}
                    >
                      {s.val}
                    </p>
                  </div>
                ))}
              </div>

              {view === "timeline" && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {filteredData.map((session) => {
                    const sites = getBrowserSites(session);
                    const media = getMedia(session);
                    const maxApp = Math.max(
                      ...session.app_usage.map((a) => a.duration_mins),
                      1,
                    );
                    const maxSite = Math.max(
                      ...sites.map((s) => s.duration_mins),
                      1,
                    );
                    const maxMedia = Math.max(
                      ...media.map((m) => m.duration_mins),
                      1,
                    );

                    const totalAppMins = session.app_usage.reduce(
                      (sum, a) => sum + a.duration_mins,
                      0,
                    );
                    const totalSiteMins = sites.reduce(
                      (sum, s) => sum + s.duration_mins,
                      0,
                    );
                    const totalMediaMins = media.reduce(
                      (sum, m) => sum + m.duration_mins,
                      0,
                    );

                    return (
                      <div key={session.id} style={card}>
                        {/* Session Header */}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 12,
                            padding: "16px 20px",
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 12,
                              background: "#f5f3ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <User
                              style={{
                                width: 16,
                                height: 16,
                                color: "#7c3aed",
                              }}
                            />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#1e293b",
                              }}
                            >
                              {session.employee_name}
                            </p>
                            <p style={{ fontSize: 11, color: "#94a3b8" }}>
                              {fmtDate(session.date)}
                            </p>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                              marginLeft: "auto",
                            }}
                          >
                            <Chip
                              label="Tracked"
                              value={fmtMins(session.total_tracked_mins)}
                            />
                            <Chip
                              label="Active"
                              value={fmtMins(session.active_mins)}
                              valueClass="text-emerald-600"
                            />
                            <Chip
                              label="Idle"
                              value={fmtMins(session.idle_mins)}
                              valueClass="text-orange-500"
                            />
                            <Chip
                              label="Login"
                              value={fmtTime(session.first_activity)}
                            />
                            <Chip
                              label="Logout"
                              value={fmtTime(session.last_activity)}
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            padding: 20,
                            display: "flex",
                            flexDirection: "column",
                            gap: 24,
                          }}
                        >
                          {/* Applications Section with Total Time */}
                          {session.app_usage.length > 0 && (
                            <div>
                              <SectionHeader
                                icon={
                                  <AppWindow
                                    style={{
                                      width: 13,
                                      height: 13,
                                      color: "#94a3b8",
                                    }}
                                  />
                                }
                                title="Applications (Click to see timeline)"
                                totalMins={totalAppMins}
                                color="#7c3aed"
                              />
                              <div
                                style={{
                                  maxHeight: 400,
                                  overflowY: "auto",
                                  paddingRight: 4,
                                  scrollbarWidth: "thin",
                                  border: "1px solid #f1f5f9",
                                  borderRadius: 12,
                                  background: "#fafafa",
                                }}
                              >
                                {session.app_usage.map((a, i) => (
                                  <ClickableAppRow
                                    key={i}
                                    app={a}
                                    max={maxApp}
                                    timeline={session.timeline}
                                    isExpanded={isAppExpanded(
                                      session.id,
                                      a.app_name,
                                    )}
                                    onToggle={() =>
                                      toggleApp(session.id, a.app_name)
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Sites Section with Total Time */}
                          {sites.length > 0 && (
                            <div>
                              <SectionHeader
                                icon={
                                  <Globe
                                    style={{
                                      width: 13,
                                      height: 13,
                                      color: "#94a3b8",
                                    }}
                                  />
                                }
                                title="Sites Visited"
                                totalMins={totalSiteMins}
                                color="#3b82f6"
                              />
                              <div
                                style={{
                                  maxHeight: 250,
                                  overflowY: "auto",
                                  paddingRight: 4,
                                  scrollbarWidth: "thin",
                                  border: "1px solid #f1f5f9",
                                  borderRadius: 12,
                                  padding: "8px 12px",
                                }}
                              >
                                {sites.map((s, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "6px 0",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: "#475569",
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {truncateText(s.title, 40)}
                                    </span>
                                    <div
                                      style={{
                                        width: 80,
                                        height: 6,
                                        background: "#f1f5f9",
                                        borderRadius: 99,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          height: "100%",
                                          borderRadius: 99,
                                          background: "#60a5fa",
                                          width: `${(s.duration_mins / maxSite) * 100}%`,
                                        }}
                                      />
                                    </div>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 500,
                                        color: "#3b82f6",
                                        minWidth: 52,
                                        textAlign: "right",
                                      }}
                                    >
                                      {fmtMins(s.duration_mins)}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 9,
                                        color: "#94a3b8",
                                        width: 28,
                                        textAlign: "right",
                                      }}
                                    >
                                      {s.visits}v
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Media Section with Total Time and Dropdown */}
                          {media.length > 0 && (
                            <div>
                              <SectionHeader
                                icon={
                                  <Youtube
                                    style={{
                                      width: 13,
                                      height: 13,
                                      color: "#ef4444",
                                    }}
                                  />
                                }
                                title="Media Detected (Click to see details)"
                                totalMins={totalMediaMins}
                                color="#ef4444"
                              />
                              <div
                                style={{
                                  maxHeight: 400,
                                  overflowY: "auto",
                                  paddingRight: 4,
                                  scrollbarWidth: "thin",
                                  border: "1px solid #f1f5f9",
                                  borderRadius: 12,
                                  background: "#fafafa",
                                }}
                              >
                                {media.map((m, i) => (
                                  <ClickableMediaRow
                                    key={m.id}
                                    media={m}
                                    max={maxMedia}
                                    isExpanded={isMediaExpanded(
                                      session.id,
                                      m.id,
                                    )}
                                    onToggle={() =>
                                      toggleMedia(session.id, m.id)
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Idle Periods */}
                          {session.idle_periods.length > 0 && (
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 12,
                                }}
                              >
                                <Hourglass
                                  style={{
                                    width: 13,
                                    height: 13,
                                    color: "#94a3b8",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#374151",
                                  }}
                                >
                                  Idle Periods
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 6,
                                }}
                              >
                                {session.idle_periods.map((p, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      padding: "8px 12px",
                                      background: "#f8fafc",
                                      borderRadius: 10,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        fontSize: 12,
                                        color: "#475569",
                                      }}
                                    >
                                      <Clock
                                        style={{
                                          width: 12,
                                          height: 12,
                                          color: "#94a3b8",
                                        }}
                                      />
                                      <span style={{ fontFamily: "monospace" }}>
                                        {fmtTime(p.start_time)}
                                      </span>
                                      <span style={{ color: "#cbd5e1" }}>
                                        →
                                      </span>
                                      <span style={{ fontFamily: "monospace" }}>
                                        {fmtTime(p.end_time)}
                                      </span>
                                    </div>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#f97316",
                                      }}
                                    >
                                      {fmtMins(p.duration_mins)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {view === "summary" && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {empSummaries().map((emp) => {
                    const eff =
                      emp.total_tracked_mins > 0
                        ? (emp.active_mins / emp.total_tracked_mins) * 100
                        : 0;
                    return (
                      <div key={emp.employeeID} style={card}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "16px 20px",
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 12,
                              background: "#f5f3ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Users
                              style={{
                                width: 16,
                                height: 16,
                                color: "#7c3aed",
                              }}
                            />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <p
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#1e293b",
                              }}
                            >
                              {emp.employee_name}
                            </p>
                            <p style={{ fontSize: 11, color: "#94a3b8" }}>
                              {emp.employeeID}
                            </p>
                          </div>
                          <span
                            style={{
                              fontSize: 10,
                              padding: "3px 8px",
                              borderRadius: 99,
                              background: "#f0fdf4",
                              color: "#16a34a",
                              fontWeight: 500,
                            }}
                          >
                            {emp.data.length} session
                            {emp.data.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div style={{ padding: 20 }}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(100px, 1fr))",
                              gap: 12,
                              marginBottom: 20,
                            }}
                          >
                            {[
                              {
                                label: "Tracked",
                                val: fmtMins(emp.total_tracked_mins),
                                c: "#1e293b",
                              },
                              {
                                label: "Active",
                                val: fmtMins(emp.active_mins),
                                c: "#16a34a",
                              },
                              {
                                label: "Idle",
                                val: fmtMins(emp.idle_mins),
                                c: "#f97316",
                              },
                              {
                                label: "Efficiency",
                                val: `${eff.toFixed(0)}%`,
                                c: "#2563eb",
                              },
                            ].map((s) => (
                              <div
                                key={s.label}
                                style={{
                                  background: "#f8fafc",
                                  borderRadius: 12,
                                  padding: 12,
                                  textAlign: "center",
                                }}
                              >
                                <p style={{ fontSize: 10, color: "#94a3b8" }}>
                                  {s.label}
                                </p>
                                <p
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: s.c,
                                    marginTop: 2,
                                  }}
                                >
                                  {s.val}
                                </p>
                              </div>
                            ))}
                          </div>
                          <p
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: "#94a3b8",
                              marginBottom: 8,
                            }}
                          >
                            Sessions
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                            {emp.data.map((s) => (
                              <div
                                key={s.id}
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 8,
                                  padding: "8px 12px",
                                  background: "#f8fafc",
                                  borderRadius: 10,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    fontSize: 11,
                                    color: "#475569",
                                  }}
                                >
                                  <Calendar
                                    style={{
                                      width: 11,
                                      height: 11,
                                      color: "#94a3b8",
                                    }}
                                  />
                                  <span>{fmtDate(s.date)}</span>
                                  <span style={{ color: "#cbd5e1" }}>·</span>
                                  <span style={{ fontFamily: "monospace" }}>
                                    {fmtTime(s.first_activity)}
                                  </span>
                                  <span>→</span>
                                  <span style={{ fontFamily: "monospace" }}>
                                    {fmtTime(s.last_activity)}
                                  </span>
                                </div>
                                <div style={{ display: "flex", gap: 12 }}>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      color: "#16a34a",
                                    }}
                                  >
                                    {fmtMins(s.active_mins)}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      color: "#f97316",
                                    }}
                                  >
                                    {fmtMins(s.idle_mins)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityDashboard;
